import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Discrepancy, Manuscript, ManuscriptParagraph, ManuscriptToken, PageFrame } from "../api/types";
import { anchored } from "../lib/anchor";
import type { ScriptMark } from "../lib/scriptNotes";
import { Icon } from "./icons";
import { MarginNote, MarkPopover, inMargin, tokenRange, useMarginPlacement, useTokenIndex, useWordDecorations } from "./ScriptMarksLayer";
import { OriginalPages } from "./OriginalPages";
import { useFindHighlights, type PaneFind } from "./FindMode";
import { useManuscriptPages, usePageWords } from "../lib/pageView";
import { findable, useFind } from "../lib/find";

interface Mark {
  start: number;
  end: number; // == start for an insertion caret (zero-width)
  id: string;
  type: string;
  colour?: string; // the flag's current category, when it differs from its type
  caret?: boolean; // "added" insertion point — no manuscript text of its own
  title?: string; // tooltip (the inserted words) for carets
}

// What a manuscript text selection resolves to: the selected fragment (whole
// tokens), its global token span, surrounding words for context, the page, and
// the audio span — enough to create a flag anchored exactly like an engine one.
export interface SelectionFlagInput {
  text: string;                  // selected fragment (joined whole tokens)
  query: string;                 // the raw selected string (for pronunciation lookup)
  tokenIndices: number[];        // manuscript global token indices
  page: number | null;
  startTime: number | null;
  endTime: number | null;
  contextBefore: string;
  contextAfter: string;
}

// Pronunciation references, opened in a new tab with the selected phrase. All
// handle multi-word phrases (Youglish/Forvo natively; the dictionaries search).
const PRON_SOURCES: { label: string; url: (q: string) => string }[] = [
  { label: "Youglish", url: (q) => `https://youglish.com/pronounce/${encodeURIComponent(q)}/english` },
  { label: "Forvo", url: (q) => `https://forvo.com/search/${encodeURIComponent(q)}/` },
  { label: "howjsay", url: (q) => `https://howjsay.com/how-to-pronounce-${encodeURIComponent(q)}` },
  { label: "Cambridge", url: (q) => `https://dictionary.cambridge.org/us/search/english/direct/?q=${encodeURIComponent(q)}` },
];

const MS_VIEW_KEY = "mb.manuscript.view";
const MS_NUMS_KEY = "mb.manuscript.lineNumbers";
const MS_RAGGED_KEY = "mb.manuscript.ragged";
const MS_ZOOM_KEY = "mb.manuscript.zoom";
const MS_MARKS_KEY = "mb.manuscript.scriptMarks";
const MS_COMMENTS_KEY = "mb.manuscript.comments";
// Formatted view zoom, as a share of the size the pages fit the pane at.
const ZOOM_STEPS = [0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3];
// The tightest a justified line's spaces are set, as a share of a space: tight
// in print, still plainly a gap. Below it words ran together.
const MIN_SPACE = 0.7;
// Layout recorded before this extractor lacks italics and the newer line
// codes, or (before v11) reads some ragged paragraph openings as centred
// lines, so a chapter proofed under it asks for a fresh layout instead.
const MIN_LAYOUT_VERSION = 11;
const readPref = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const writePref = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private window */ } };

// What scrolls the manuscript: the nearest ancestor that scrolls, else the page.
function scrollerOf(el: HTMLElement): HTMLElement {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const cs = getComputedStyle(p);
    if (/auto|scroll/.test(cs.overflowY + cs.overflowX)) return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}
// ...and the part of the screen it shows.
function viewOf(sc: HTMLElement): { left: number; top: number; width: number; height: number } {
  if (sc === document.scrollingElement || sc === document.documentElement) {
    return { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
  }
  const r = sc.getBoundingClientRect();
  return { left: r.left + sc.clientLeft, top: r.top + sc.clientTop, width: sc.clientWidth, height: sc.clientHeight };
}
// Whether a selected flag is already in front of the reader: all of a highlighted
// word, with a little room around it; or a good part of a page (a flag with no
// word of its own is shown by its page).
function inView(sc: HTMLElement, el: Element, word: boolean): boolean {
  const v = viewOf(sc);
  const r = el.getBoundingClientRect();
  if (word) {
    const m = 24;
    return r.top >= v.top + m && r.bottom <= v.top + v.height - m &&
      r.left >= v.left && r.right <= v.left + v.width;
  }
  const overlap = Math.min(r.bottom, v.top + v.height) - Math.max(r.top, v.top);
  return overlap >= Math.min(r.height, v.height) / 2;
}

// How the printed page set a paragraph (see extract_script._split_paragraphs_rich).
type ParaLayout = Pick<ManuscriptParagraph, "line_starts" | "para_starts" | "line_fmt" | "italic">;
export interface LayoutResult {
  layout: Record<string, ParaLayout>;
  frames?: Record<string, PageFrame>;   // keyed by the page the report shows
  matched: number;
  total: number;
}
const CONTEXT_WORDS = 8; // words of context captured each side of a selection flag

interface Props {
  manuscript: Manuscript;
  discrepancies: Discrepancy[]; // already filtered to what should highlight
  // Flag id → its current category (the one its card shows). A highlight takes
  // its colour from this, so re-labelling a flag re-colours its words too.
  categories?: Map<string, string>;
  activeId: string | null;
  selectedId: string | null;
  selectTick?: number; // bumped on every select so re-clicking re-scrolls
  onSelect: (id: string) => void;
  onPlayAt?: (time: number) => void; // click any word → play from its audio time
  onFlagSelection?: (sel: SelectionFlagInput) => void; // "Flag here" from a text selection
  // When set, each "p. N" label links to that page of the original PDF in a new
  // tab. The pane shows PRINTED page numbers; the PDF indexes physical pages, so
  // physical = printed − pageOffset (printed = physical + offset).
  pdfUrl?: string;
  pageOffset?: number;
  // Book layout for a chapter proofed before the layout was recorded: fetched
  // only when the reader actually switches to it.
  loadLayout?: () => Promise<LayoutResult>;
  // The script's own marks on this chapter's pages (notes, comments, struck
  // words, highlights, pen marks) - laid over the text by ScriptMarksLayer -
  // and where to ask for the original page around one.
  scriptMarks?: ScriptMark[];
  projectId?: string;
  chapterId?: string;
  // Find mode: this file's hits, picked out in the text, and the one you're on
  // shown; and "Find all of these in the manuscript" from a text selection.
  find?: PaneFind;
  onFindAll?: (sel: SelectionFlagInput) => void;
}

// Resolve a click point to its character offset within a paragraph element,
// across the small browser differences in the caret-position API.
function caretCharOffset(paraEl: HTMLElement, x: number, y: number): number | null {
  const doc = document as any;
  let node: Node | null = null;
  let offset = 0;
  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) { node = pos.offsetNode; offset = pos.offset; }
  } else if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) { node = r.startContainer; offset = r.startOffset; }
  }
  if (!node || !paraEl.contains(node)) return null;
  // Sum the text length of every text node before the caret node.
  let total = 0;
  const walker = document.createTreeWalker(paraEl, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    if (n === node) return total + offset;
    total += (n.textContent || "").length;
  }
  return total + offset;
}

export function ManuscriptPane({ manuscript, discrepancies, categories, activeId, selectedId, selectTick, onSelect, onPlayAt, onFlagSelection, pdfUrl, pageOffset = 0, loadLayout, scriptMarks, projectId, chapterId, find, onFindAll }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Floating menu shown when the reviewer selects manuscript text.
  const [selMenu, setSelMenu] = useState<{ sel: SelectionFlagInput; rect: DOMRect } | null>(null);

  // Book layout: the paragraphs and line breaks the printed page used, instead
  // of one flowing block. Remembered per user — both the layout and the line
  // numbers — so it is set once. Wrapped: storage throws in private windows.
  const [formatted, setFormatted] = useState<boolean>(() => readPref(MS_VIEW_KEY) === "formatted");
  const [numbered, setNumbered] = useState<boolean>(() => readPref(MS_NUMS_KEY) === "1");
  const [ragged, setRagged] = useState<boolean>(() => readPref(MS_RAGGED_KEY) === "1");
  const setView = (on: boolean) => { setFormatted(on); writePref(MS_VIEW_KEY, on ? "formatted" : "flowing"); };
  const setNums = (on: boolean) => { setNumbered(on); writePref(MS_NUMS_KEY, on ? "1" : "0"); };
  const setRag = (on: boolean) => { setRagged(on); writePref(MS_RAGGED_KEY, on ? "1" : "0"); };

  // The script's marks: on unless turned off; Word comments - notes on the
  // writing, not direction for the recording - off unless turned on.
  const [marksOn, setMarksOn] = useState<boolean>(() => readPref(MS_MARKS_KEY) !== "0");
  const [commentsOn, setCommentsOn] = useState<boolean>(() => readPref(MS_COMMENTS_KEY) === "1");
  const setMarks = (on: boolean) => { setMarksOn(on); writePref(MS_MARKS_KEY, on ? "1" : "0"); };
  const setComments = (on: boolean) => { setCommentsOn(on); writePref(MS_COMMENTS_KEY, on ? "1" : "0"); };

  // The Original view: the uploaded PDF's own pages, flags on their words
  // (OriginalPages). A third view, not remembered - a report opens on the text.
  const [original, setOriginal] = useState(false);
  const originalOk = !!projectId && !!pdfUrl;
  const { data: pdfPages } = useManuscriptPages(originalOk ? projectId : undefined);
  const { data: pageWords } = usePageWords(projectId, chapterId, original);
  const [flagsOn, setFlagsOn] = useState(true);
  const hasComments = !!scriptMarks?.some((m) => m.kind === "comment");
  const hasMarks = !!scriptMarks?.some((m) => m.kind !== "comment");
  const shownMarks = useMemo(
    () => (original ? [] : (scriptMarks ?? []).filter((m) => (m.kind === "comment" ? commentsOn : marksOn))),
    [scriptMarks, marksOn, commentsOn, original]
  );
  // Margin notes, by the page their word is on.
  const notesByPage = useMemo(() => {
    const pageOf = new Map<number, number>();
    for (const t of manuscript.tokens || []) pageOf.set(t.global_index, t.page);
    const out = new Map<number, ScriptMark[]>();
    for (const m of shownMarks) {
      if (!inMargin(m)) continue;
      const pg = pageOf.get(m.at);
      if (pg == null) continue;
      out.set(pg, [...(out.get(pg) ?? []), m]);
    }
    return out;
  }, [shownMarks, manuscript]);
  const gutter = notesByPage.size > 0;
  const [focusMark, setFocusMark] = useState<ScriptMark | null>(null);
  const [openMark, setOpenMark] = useState<{ mark: ScriptMark; rect: DOMRect } | null>(null);

  // Zoom, remembered like the rest. The page grows or shrinks around what the
  // reader is looking at — the line at the pointer (a pinch) or at the middle
  // of the pane (the buttons) stays where it was — instead of jumping.
  const [zoom, setZoom] = useState<number>(() => {
    const z = Number(readPref(MS_ZOOM_KEY));
    return ZOOM_STEPS.includes(z) ? z : 1;
  });
  // `d`: the point's distance from the middle of the (centred) pages, which
  // scales by the zoom's ratio `r`.
  const zoomAnchor = useRef<{ line: HTMLElement | null; top: number; x: number; d: number; r: number } | null>(null);
  const zoomTo = (z: number, at?: { x: number; y: number }) => {
    const root = rootRef.current;
    if (!root || z === zoom) return;
    const sc = scrollerOf(root);
    const view = viewOf(sc);
    const x = at ? at.x - view.left : view.width / 2;
    const y = at ? at.y : view.top + view.height / 2;
    let line: HTMLElement | null = null;
    for (const l of root.querySelectorAll<HTMLElement>(".ms-line")) {
      if (l.getBoundingClientRect().bottom >= y) { line = l; break; }
    }
    zoomAnchor.current = {
      line, top: line ? line.getBoundingClientRect().top : 0,
      x, d: sc.scrollLeft + x - sc.scrollWidth / 2, r: z / zoom,
    };
    setZoom(z);
    writePref(MS_ZOOM_KEY, String(z));
  };
  const stepZoom = (dir: 1 | -1, at?: { x: number; y: number }) => {
    const next = dir > 0 ? ZOOM_STEPS.find((s) => s > zoom) : [...ZOOM_STEPS].reverse().find((s) => s < zoom);
    if (next !== undefined) zoomTo(next, at);
  };
  const stepZoomRef = useRef(stepZoom);
  stepZoomRef.current = stepZoom;

  // Layout fetched for a chapter proofed before the current layout existed.
  // Keyed like marks ("page:paragraph"); dropped whenever the manuscript
  // changes — a different chapter is a different text.
  const [fetched, setFetched] = useState<Map<string, ParaLayout> | null>(null);
  const [fetchedFrames, setFetchedFrames] = useState<Map<string, PageFrame> | null>(null);
  const [layoutState, setLayoutState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [coverage, setCoverage] = useState<{ matched: number; total: number } | null>(null);
  useEffect(() => { setFetched(null); setFetchedFrames(null); setLayoutState("idle"); setCoverage(null); }, [manuscript]);

  const hasInline = useMemo(
    () => manuscript.pages.some((pg) => pg.paragraphs.some((p) => !!p.line_starts?.length)),
    [manuscript]
  );
  const inlineCurrent = hasInline && (manuscript._extractor_version ?? 0) >= MIN_LAYOUT_VERSION;
  const needFetch = !inlineCurrent && !!loadLayout;
  // Offered when the layout is here, or can be fetched.
  const canFormat = hasInline || !!loadLayout;
  const showBook = formatted && canFormat && !original;

  // Ask once per manuscript. Guarded by a ref rather than by the state it sets:
  // with the state in the dependencies, flipping it to "loading" would re-run
  // this effect, mark the request stale, and drop its answer.
  const requestedFor = useRef<unknown>(null);
  useEffect(() => {
    if (!showBook || !needFetch || !loadLayout) return;
    if (requestedFor.current === manuscript) return;
    const forMs = manuscript;
    requestedFor.current = forMs;
    setLayoutState("loading");
    loadLayout()
      .then((res) => {
        if (requestedFor.current !== forMs) return;  // moved to another chapter meanwhile
        setFetched(new Map(Object.entries(res.layout || {})));
        setFetchedFrames(new Map(Object.entries(res.frames || {})));
        setCoverage({ matched: res.matched, total: res.total });
        setLayoutState("done");
      })
      .catch(() => {
        if (requestedFor.current !== forMs) return;
        requestedFor.current = null;                   // switching again retries
        setLayoutState("error");
      });
  }, [showBook, needFetch, loadLayout, manuscript]);

  // Fresh layout wins over an older one carried inline; while it's loading,
  // the older inline layout is still better than none.
  const layoutFor = (pageNo: number, pi: number, para: ManuscriptParagraph): ParaLayout | null =>
    fetched?.get(`${pageNo}:${pi}`) ?? (para.line_starts?.length ? para : null);
  const frameFor = (page: { page_number: number; frame?: PageFrame }): PageFrame | null =>
    fetchedFrames?.get(String(page.page_number)) ?? page.frame ?? null;

  // Setting the column so a printed line never re-breaks — the promise of this
  // view — and so justified text reads like print rather than full of gaps.
  //
  // A line's words are fixed, so justifying it to a column wider than they are
  // stretches every space: set to the widest line, spaces ran at 1.6–2.5× and
  // dozens of lines were too loose to justify at all. Print absorbs the same
  // variation with a touch of letter spacing as well as word spacing, and so
  // does this: the column is set to the book's typical FULL line, and each line
  // takes up its difference first in letter spacing (+2.5% / −2%, invisible),
  // then in its spaces. On the test book that holds word spaces at 1.00× (90th
  // pct 1.13×), with none left ragged or squeezed. A line that would still need
  // its spaces more than doubled (a line of verse, a short line) is left as set.
  //
  // The typical full line is the median of the lines near the longest, not of
  // every line: a manuscript typed in Word ends many lines with a manual line
  // break, which print stretches to the margin, and those short lines pulled the
  // median in until every real full line was squeezed - Alpha Female's spaces
  // went below nothing and its words ran together (Nov 2026). Nor is a space ever
  // squeezed below MIN_SPACE of itself: a line still too long takes its page's
  // type down a touch, as an outlying page does.
  //
  // Ragged right keeps the column at the widest line and adjusts nothing. If the
  // column won't fit the pane the type shrinks; a page holding an outlier (a
  // contents line, a URL) shrinks on its own, so one odd page can't shrink the
  // book. Re-run when the pane changes width — and when the book's font arrives,
  // since measuring in the fallback font sets the column too wide.
  //
  // Zoom scales the fitted size. Zoomed past the pane, the pages scroll
  // sideways and the bar stays pinned across the visible width (--ms-view-w).
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sc = scrollerOf(root);
    const holder = root.parentElement ?? root;
    const pages = () => Array.from(root.querySelectorAll<HTMLElement>(".ms-page"));
    const clear = () => {
      root.style.removeProperty("--ms-fit");
      root.style.removeProperty("--ms-measure");
      root.style.removeProperty("--ms-sheet-h");
      root.style.removeProperty("--ms-view-w");
      root.querySelectorAll<HTMLElement>(".ms-after-head").forEach((el) => el.style.removeProperty("margin-top"));
      pages().forEach((pg) => pg.style.removeProperty("--ms-pfit"));
      root.querySelectorAll<HTMLElement>(".ms-line.jl").forEach((l) => {
        l.style.removeProperty("--ls");
        l.style.removeProperty("--ws");
        delete l.dataset.nj;
      });
    };
    if (!showBook) { clear(); return; }
    const place = () => {
      clear();
      // Measured at the zoom: type doesn't scale exactly, and full lines
      // measured at 100% and scaled to 150% ran 2px past their column.
      root.style.setProperty("--ms-fit", String(zoom));
      root.classList.add("ms-measuring");
      // The room is the sheet's, never a paragraph's: a paragraph is centred in
      // its sheet, so while measuring it shrinks to its own lines, and a chapter
      // opening on "To my girls xxx" took that one short line as the room and
      // set the whole chapter at a fifth of its size. Nor the sheet's own width:
      // zoomed in on a narrow pane its margins alone outgrow the pane, and the
      // sheet widens to hold them. The room is the column the sheet sits in,
      // inside its border; its margins (em) scale with the type, so by the fit.
      const sheet = pages()[0];
      const rootCs = getComputedStyle(root);
      const room = sheet
        ? root.clientWidth - parseFloat(rootCs.paddingLeft) - parseFloat(rootCs.paddingRight)
          - (sheet.offsetWidth - sheet.clientWidth)
        : 0;
      const sheetCs = sheet ? getComputedStyle(sheet) : null;
      const margins = sheetCs ? parseFloat(sheetCs.paddingLeft) + parseFloat(sheetCs.paddingRight) : 0;
      const spaceAt = new Map<string, number>();
      const rows: { el: HTMLElement; w: number; g: number; c: number; s: number; fs: number }[] = [];
      let widest = 0;
      root.querySelectorAll<HTMLElement>(".ms-line").forEach((l) => {
        const w = l.getBoundingClientRect().width;
        if (w > widest) widest = w;
        if (!l.classList.contains("jl")) return;
        const cs = getComputedStyle(l);
        let s = spaceAt.get(cs.fontSize + cs.fontStyle);
        if (s === undefined) {
          const probe = document.createElement("span");
          probe.style.cssText = "position:absolute;visibility:hidden;white-space:pre";
          probe.textContent = " ";
          l.appendChild(probe);
          s = probe.getBoundingClientRect().width;
          probe.remove();
          spaceAt.set(cs.fontSize + cs.fontStyle, s);
        }
        const text = (l.textContent || "").trim();
        rows.push({ el: l, w, g: (text.match(/\S+/g) || []).length - 1, c: text.length, s, fs: parseFloat(cs.fontSize) });
      });
      root.classList.remove("ms-measuring");

      const core = rows.filter((r) => r.g > 0).map((r) => r.w).sort((a, b) => a - b);
      const med = core.length ? core[Math.floor(core.length / 2)] : 0;
      const body = core.filter((w) => w >= med * 0.8 && w <= med * 1.25);
      // The full lines: those near the longest (the 90th percentile).
      const top = core.length ? core[Math.min(core.length - 1, Math.floor(core.length * 0.9))] : 0;
      const full = core.filter((w) => w >= top * 0.85);
      const fullMed = full.length ? full[Math.floor(full.length / 2)] : med;
      let measure = widest;
      if (body.length) measure = ragged ? body[body.length - 1] : fullMed;
      if (!measure || !room) return;
      // The fitted size (never above print size) times the zoom; `k` takes the
      // widths measured at the zoom to it. Less 2px: the column is rounded up
      // by as much below.
      const k = Math.min(1, zoom * (room - 2) / (measure + margins));
      const f = k * zoom;
      const m = measure * k;
      root.style.setProperty("--ms-fit", String(f));
      root.style.setProperty("--ms-measure", `${Math.ceil(m) + 1}px`);
      const hcs = getComputedStyle(holder);
      root.style.setProperty("--ms-view-w",
        `${holder.clientWidth - parseFloat(hcs.paddingLeft) - parseFloat(hcs.paddingRight)}px`);

      if (!ragged) {
        for (const r of rows) {
          const w = r.w * k, s = r.s * k, fs = r.fs * k;
          const extra = m - w;
          const ls = Math.max(-0.02 * fs, Math.min(0.025 * fs, r.c ? extra / r.c : 0));
          const rest = extra - ls * r.c;
          if (r.g <= 0 || 1 + rest / (r.g * s) > 2.2) { r.el.dataset.nj = "1"; continue; }
          r.el.style.setProperty("--ls", `${ls.toFixed(3)}px`);
          // Never tighter than MIN_SPACE of a space: a line that won't fit even
          // so runs past the column, and the page fit below takes it in.
          if (rest < 0) r.el.style.setProperty("--ws", `${Math.max(rest / r.g, -(1 - MIN_SPACE) * s).toFixed(3)}px`);
        }
      }
      for (const pg of pages()) {
        let worst = 1;
        pg.querySelectorAll<HTMLElement>(".ms-line").forEach((l) => {
          if (l.clientWidth) worst = Math.max(worst, l.scrollWidth / l.clientWidth);
        });
        if (worst > 1.002) pg.style.setProperty("--ms-pfit", String(Math.max(0.6, 0.995 / worst)));
      }
      // Every page the same size, as in a book: the height of a TYPICAL full
      // page — the commonest height among ordinary (not sunk) pages. Not the
      // tallest: books run the odd page a line long, and sizing to those would
      // leave every ordinary page a line short and every chapter opening ending
      // lower than the pages around it. A short page at a chapter's end shows
      // its white space below the text; a page a line long simply grows.
      const plain = pages().filter((pg) => !pg.classList.contains("ms-sunk") && !pg.classList.contains("ms-sunk-full"));
      const counts = new Map<number, number>();
      for (const pg of plain) {
        const h = Math.round(pg.offsetHeight / 2) * 2;
        if (h > 0) counts.set(h, (counts.get(h) || 0) + 1);
      }
      const typical = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0]?.[0];
      if (typical) root.style.setProperty("--ms-sheet-h", `${typical}px`);

      // A chapter opening that ran to the foot in print: the heading keeps its
      // own sink (in lines, as recorded), and whatever space is left goes into
      // the gap between heading and text — as print does — so the text still
      // ends where a full page's does.
      for (const pg of pages()) {
        if (!pg.classList.contains("ms-sunk-full")) continue;
        const after = pg.querySelector<HTMLElement>(".ms-after-head");
        const paras = pg.querySelectorAll<HTMLElement>(".ms-para");
        if (!after || !paras.length) continue;
        const cs = getComputedStyle(pg);
        const contentBottom = pg.getBoundingClientRect().bottom - parseFloat(cs.paddingBottom) - parseFloat(cs.borderBottomWidth);
        const leftover = contentBottom - paras[paras.length - 1].getBoundingClientRect().bottom;
        if (leftover <= 1) continue;
        const prev = after.previousElementSibling as HTMLElement | null;
        const gap = prev ? after.getBoundingClientRect().top - prev.getBoundingClientRect().bottom
                         : parseFloat(getComputedStyle(after).marginTop) || 0;
        after.style.marginTop = `${gap + leftover}px`;
      }
    };
    // Measuring collapses the pages for a moment, which can pull the pane's
    // scroll back to the start; put it back where it was.
    const fit = () => {
      const top = sc.scrollTop, left = sc.scrollLeft;
      try { place(); } finally { sc.scrollTop = top; sc.scrollLeft = left; }
    };
    fit();
    const a = zoomAnchor.current;
    if (a) {
      zoomAnchor.current = null;
      if (a.line?.isConnected) sc.scrollTop += a.line.getBoundingClientRect().top - a.top;
      sc.scrollLeft = sc.scrollWidth / 2 + a.d * a.r - a.x;
    }
    // A timer, not requestAnimationFrame: rAF never fires in a background tab,
    // so a report opened behind another would keep a fallback-font column.
    // Watched on the pane, not the pages: zoomed in, the pages are as wide as
    // themselves, not the pane.
    let timer = 0;
    let lastW = holder.clientWidth;
    const refit = () => { window.clearTimeout(timer); timer = window.setTimeout(fit, 0); };
    const ro = new ResizeObserver(() => {
      if (holder.clientWidth === lastW) return;   // only width changes can re-break
      lastW = holder.clientWidth;
      refit();
    });
    ro.observe(holder);
    // Measured in a fallback font, the column comes out too wide (455px vs
    // 432px on the test book) and every space loosens. `fonts.ready` can resolve
    // before the book face has even been requested, so ask for it outright —
    // roman and italic, at the size in use — and re-fit once it's here.
    let live = true;
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    const host = root.querySelector<HTMLElement>(".ms-para");
    if (fonts && host) {
      const cs = getComputedStyle(host);
      Promise.all([
        fonts.load(`${cs.fontSize} ${cs.fontFamily}`),
        fonts.load(`italic ${cs.fontSize} ${cs.fontFamily}`),
      ]).then(() => { if (live) refit(); }).catch(() => { /* keep the fallback fit */ });
    }
    fonts?.addEventListener?.("loadingdone", refit);
    return () => {
      live = false;
      ro.disconnect();
      window.clearTimeout(timer);
      fonts?.removeEventListener?.("loadingdone", refit);
    };
  }, [manuscript, showBook, fetched, numbered, ragged, zoom, gutter]);

  // The script's marks beside the text, after the text has been laid out.
  const textDeps = [manuscript, showBook, fetched, numbered, ragged, zoom, shownMarks, discrepancies];

  // ---- The page you're on, and keeping it across views ----------------------
  // The page passing under the bar. Read on scroll, a frame at a time, by a
  // binary search over the pages (they are in order), so a 400-page book costs
  // a handful of reads.
  const [curPage, setCurPage] = useState<number | null>(null);
  const barLine = () => {
    const root = rootRef.current;
    const bar = root?.querySelector<HTMLElement>(".ms-viewbar");
    return (bar ? bar.getBoundingClientRect().bottom : root ? viewOf(scrollerOf(root)).top : 0) + 12;
  };
  const pageUnderBar = (): HTMLElement | null => {
    const root = rootRef.current;
    if (!root) return null;
    const pages = root.querySelectorAll<HTMLElement>(".ms-page");
    const line = barLine();
    let lo = 0, hi = pages.length - 1, best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (pages[mid].getBoundingClientRect().top <= line) { best = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return pages[Math.max(0, best)] ?? null;
  };
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const sc = scrollerOf(root);
    const target: HTMLElement | Window = sc === document.scrollingElement || sc === document.documentElement ? window : sc;
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = pageUnderBar();
      if (el) setCurPage(Number(el.dataset.page));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    target.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => { target.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [manuscript, showBook, original]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Switching views keeps you on the same page, at the same point down it;
  // to and from Original the page turns over (a fade with reduced motion).
  const keepPlace = useRef<{ page: number; frac: number; turn: boolean } | null>(null);
  const switchView = (next: "formatted" | "flowing" | "original") => {
    const now = original ? "original" : showBook ? "formatted" : "flowing";
    if (next === now) return;
    const el = pageUnderBar();
    if (el) {
      const r = el.getBoundingClientRect();
      keepPlace.current = {
        page: Number(el.dataset.page),
        frac: r.height ? Math.max(0, Math.min(1, (barLine() - r.top) / r.height)) : 0,
        turn: next === "original" || now === "original",
      };
    }
    if (next === "original") setOriginal(true);
    else { setOriginal(false); setView(next === "formatted"); }
  };
  useLayoutEffect(() => {
    const a = keepPlace.current;
    const root = rootRef.current;
    if (!a || !root) return;
    keepPlace.current = null;
    const el = root.querySelector<HTMLElement>(`.ms-page[data-page="${a.page}"]`);
    if (!el) return;
    const sc = scrollerOf(root);
    const r = el.getBoundingClientRect();
    sc.scrollTop += r.top + a.frac * r.height - barLine();
    if (a.turn) {
      el.classList.remove("ms-turn");
      void el.offsetWidth;            // restart the animation
      el.classList.add("ms-turn");
      window.setTimeout(() => el.classList.remove("ms-turn"), 700);
    }
  }, [original, showBook]);  // eslint-disable-line react-hooks/exhaustive-deps

  const goToPage = (n: number): boolean => {
    const root = rootRef.current;
    const el = root?.querySelector<HTMLElement>(`.ms-page[data-page="${n}"]`);
    if (!root || !el) return false;
    const sc = scrollerOf(root);
    sc.scrollTop += el.getBoundingClientRect().top - barLine() + 4;
    return true;
  };
  const shownPages = manuscript.pages.map((pg) => pg.page_number);
  const pageTotal = pdfPages ? pdfPages.count + pageOffset : shownPages[shownPages.length - 1];
  const pdfPageOf = (shown: number) => Math.max(1, shown - pageOffset);
  const [pageMenu, setPageMenu] = useState(false);
  useMarginPlacement(rootRef, manuscript.tokens || [], gutter, textDeps);
  useWordDecorations(rootRef, manuscript.tokens || [], shownMarks, focusMark, textDeps);
  useFindHighlights(rootRef, manuscript.tokens || [], find, !original, textDeps);

  // Find mode: show the hit you're on - its words in the text, or its ring on
  // the original page - each time you move to it. Placed a frame after the
  // drawing (the Formatted view fits its lines first); a hit in the file still
  // opening waits for its words to be drawn.
  const tokenAt = useTokenIndex(manuscript.tokens || []);
  const findShown = useRef("");
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !find?.current) return;
    const ask = `${find.current}#${find.tick}`;
    if (findShown.current === ask) return;
    const hit = find.hits.find((h) => h.id === find.current);
    if (!hit) return;
    const raf = requestAnimationFrame(() => {
      let rect: DOMRect | null | undefined;
      if (original) rect = root.querySelector("[data-find-now]")?.getBoundingClientRect();
      else {
        const tok = tokenAt.get(hit.tokens[0]);
        rect = tok ? tokenRange(root, tok)?.getBoundingClientRect() : null;
      }
      if (!rect || (!rect.width && !rect.height)) return;
      findShown.current = ask;
      const sc = scrollerOf(root);
      const v = viewOf(sc);
      const top = barLine() + 24;
      if (rect.top >= top && rect.bottom <= v.top + v.height - 40) return;   // already in sight
      const by = rect.top - (v.top + v.height * 0.4);
      sc.scrollBy({ top: by, behavior: Math.abs(by) > v.height * 1.5 ? "auto" : "smooth" });
    });
    return () => cancelAnimationFrame(raf);
  }, [find, original, tokenAt, pageWords, ...textDeps]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Pinching a trackpad (or Ctrl + scroll) zooms the pages, not the browser.
  // A pinch arrives as a stream of small ctrl-wheel steps: one zoom step per
  // ~40 of them, toward the pointer. A mouse wheel's notch is one step.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !showBook) return;
    let acc = 0;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      acc += e.deltaY;
      if (Math.abs(acc) < 40) return;
      const dir = acc < 0 ? 1 : -1;
      acc = 0;
      stepZoomRef.current(dir, { x: e.clientX, y: e.clientY });
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [showBook]);

  // Show a flag when it is SELECTED — clicked in the list or the manuscript, or
  // reached with ↓/↑ or Enter: its highlighted word if it has one, else its page
  // (for audio-only flags like "added"/pauses that have no manuscript word).
  //
  // Each selection is acted on ONCE. `selectTick` makes re-clicking the same flag
  // a new selection; the flag list being rebuilt is not one. It is rebuilt all
  // the time — the playhead lighting up each flag it passes redraws the report —
  // and every rebuild used to pull the page back to the selected flag while you
  // were listening to a passage somewhere else. A flag already in view isn't
  // scrolled to at all: you're looking at it.
  const shownFor = useRef("");
  useEffect(() => {
    if (!selectedId || !rootRef.current) return;
    const ask = `${selectedId}#${selectTick ?? 0}`;
    if (shownFor.current === ask) return;
    const root = rootRef.current;
    let el: Element | null = root.querySelector(`mark[data-id="${selectedId}"]`);
    const word = !!el;
    if (!el) {
      const d = discrepancies.find((x) => x.id === selectedId);
      if (d?.page != null) el = root.querySelector(`.ms-page[data-page="${d.page}"]`);
    }
    if (!el) {
      // Not on the page yet while the chapter is still drawing — the flags
      // arriving bring us back here. Once it is drawn, there is nothing to show.
      if (root.querySelector(".ms-para")) shownFor.current = ask;
      return;
    }
    shownFor.current = ask;
    if (!inView(scrollerOf(root), el, word)) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selectedId, selectTick, discrepancies]);

  // Build: "page:paraIdx" -> marks. Span marks come from a discrepancy's
  // manuscript tokens; "added" flags (no manuscript text of their own) become
  // a zero-width caret at the token the insertion sits before.
  const marksByPara = useMemo(() => {
    const tokens = manuscript.tokens || [];
    const byGlobal = new Map<number, (typeof tokens)[number]>();
    for (const t of tokens) byGlobal.set(t.global_index, t);

    // Tokens that carry an audio time, ascending — used to anchor "pause" flags
    // (which have no manuscript word of their own) to the word just before the
    // silence, reusing the same per-token audio times that drive click-to-play.
    const timed = tokens
      .filter((t) => t.audio_time != null)
      .sort((a, b) => (a.audio_time as number) - (b.audio_time as number));
    const tokenBeforeTime = (t: number) => {
      let lo = 0, hi = timed.length - 1, ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if ((timed[mid].audio_time as number) <= t) { ans = mid; lo = mid + 1; }
        else hi = mid - 1;
      }
      return ans >= 0 ? timed[ans] : timed[0];
    };

    const map = new Map<string, Mark[]>();
    const push = (key: string, m: Mark) => {
      const arr = map.get(key);
      if (arr) arr.push(m);
      else map.set(key, [m]);
    };

    for (const d of discrepancies) {
      // "added" → insertion caret at the anchor token.
      if (d.type === "added" && typeof d.insert_before_index === "number") {
        const tok = byGlobal.get(d.insert_before_index);
        if (tok) {
          const pos = tok.char_span_in_para[0];
          push(`${tok.page}:${tok.para_index}`, {
            start: pos, end: pos, id: d.id, type: "added", caret: true, title: d.heard_text,
          });
        }
        continue;
      }
      // "pause" → a marker at the word the narrator paused AFTER. Pauses carry no
      // manuscript word, so anchor by audio time (the token whose time is at/just
      // before the silence) and drop a zero-width marker right after that word.
      if (d.type === "pause") {
        if (typeof d.start_time === "number" && timed.length) {
          const tok = tokenBeforeTime(d.start_time);
          if (tok) {
            const pos = tok.char_span_in_para[1]; // right after that word
            push(`${tok.page}:${tok.para_index}`, {
              start: pos, end: pos, id: d.id, type: "pause", caret: true, title: d.heard_text,
            });
          }
        }
        continue;
      }
      // Everything else → highlight its manuscript word(s).
      const idxs = d.manuscript_token_indices;
      if (!idxs || idxs.length === 0) continue;
      const groups = new Map<string, [number, number]>();
      for (const gi of idxs) {
        const tok = byGlobal.get(gi);
        if (!tok) continue;
        const key = `${tok.page}:${tok.para_index}`;
        const [s, e] = tok.char_span_in_para;
        const cur = groups.get(key);
        if (cur) groups.set(key, [Math.min(cur[0], s), Math.max(cur[1], e)]);
        else groups.set(key, [s, e]);
      }
      for (const [key, [s, e]] of groups) {
        push(key, { start: s, end: e, id: d.id, type: d.type, colour: categories?.get(d.id) });
      }
    }
    // Sort by position; at the same position a caret precedes a span so the
    // insertion marker sits just before the following word.
    for (const arr of map.values())
      arr.sort((a, b) => a.start - b.start || (a.caret ? 0 : 1) - (b.caret ? 0 : 1));
    return map;
  }, [manuscript, discrepancies, categories]);

  // "page:paraIdx" -> tokens (char offset + audio time), sorted, for resolving
  // a click anywhere in the manuscript to "play from that word".
  const timesByPara = useMemo(() => {
    const map = new Map<string, Array<{ char: number; time: number }>>();
    for (const t of manuscript.tokens || []) {
      if (t.audio_time == null) continue;
      const key = `${t.page}:${t.para_index}`;
      const arr = map.get(key) || [];
      arr.push({ char: t.char_span_in_para[0], time: t.audio_time });
      map.set(key, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.char - b.char);
    return map;
  }, [manuscript]);

  // Full tokens in reading order + a (page:para) → tokens-by-char index, for
  // resolving a text selection to a whole-token span.
  const { gtoks, posByGlobal, tokensByPara } = useMemo(() => {
    const gtoks = [...(manuscript.tokens || [])].sort((a, b) => a.global_index - b.global_index);
    const posByGlobal = new Map<number, number>();
    gtoks.forEach((t, i) => posByGlobal.set(t.global_index, i));
    const tokensByPara = new Map<string, ManuscriptToken[]>();
    for (const t of gtoks) {
      const k = `${t.page}:${t.para_index}`;
      const arr = tokensByPara.get(k) || [];
      arr.push(t);
      tokensByPara.set(k, arr);
    }
    for (const arr of tokensByPara.values())
      arr.sort((a, b) => a.char_span_in_para[0] - b.char_span_in_para[0]);
    return { gtoks, posByGlobal, tokensByPara };
  }, [manuscript]);

  // Resolve a selection endpoint (DOM node + offset) to the manuscript token at
  // that point. `prefer` picks the first token at/after (selection start) or the
  // last token at/before (selection end) when the point falls between words.
  function tokenAtPoint(node: Node, offset: number, prefer: "start" | "end"): ManuscriptToken | null {
    const root = rootRef.current;
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
    const paraEl = el?.closest(".ms-para") as HTMLElement | null;
    if (!paraEl || !root?.contains(paraEl)) return null;
    const page = Number(paraEl.dataset.page), para = Number(paraEl.dataset.para);
    // Character offset of the point within the paragraph text.
    let char = 0;
    const walker = document.createTreeWalker(paraEl, NodeFilter.SHOW_TEXT);
    let n: Node | null, found = false;
    while ((n = walker.nextNode())) {
      if (n === node) { char += offset; found = true; break; }
      // Skip the zero-width caret glyphs (‸/⏸) of added/pause marks — they aren't
      // part of the manuscript text, so they'd otherwise drift the char offset.
      if ((n as Text).parentElement?.classList.contains("caret")) continue;
      char += (n.textContent || "").length;
    }
    if (!found && node.nodeType !== Node.TEXT_NODE) char = 0; // selection grabbed an element edge
    const toks = tokensByPara.get(`${page}:${para}`);
    if (!toks || !toks.length) return null;
    if (prefer === "start") {
      // token containing the point, else the first one starting at/after it.
      return toks.find((t) => char < t.char_span_in_para[1]) ?? toks[toks.length - 1];
    }
    // end: token containing the point, else the last one ending at/before it.
    let last: ManuscriptToken | null = null;
    for (const t of toks) {
      if (t.char_span_in_para[0] < char) last = t;
      if (char <= t.char_span_in_para[1] && char > t.char_span_in_para[0]) return t;
    }
    return last ?? toks[0];
  }

  const join = (toks: ManuscriptToken[]) => toks.map((t) => t.text).join(" ");

  // Build the SelectionFlagInput for the current selection, if it sits inside the
  // manuscript and resolves to a token span.
  function resolveSelection(): SelectionFlagInput | null {
    const root = rootRef.current;
    const sel = window.getSelection();
    if (!root || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return null;
    const startTok = tokenAtPoint(range.startContainer, range.startOffset, "start");
    const endTok = tokenAtPoint(range.endContainer, range.endOffset, "end");
    if (!startTok || !endTok) return null;
    const lo = posByGlobal.get(startTok.global_index);
    const hi = posByGlobal.get(endTok.global_index);
    if (lo == null || hi == null || hi < lo) return null;
    const span = gtoks.slice(lo, hi + 1);
    if (!span.length) return null;
    const timed = span.filter((t) => t.audio_time != null);
    return {
      text: join(span),
      query: (sel.toString() || join(span)).trim(),
      tokenIndices: span.map((t) => t.global_index),
      page: span[0].page,
      startTime: timed.length ? (timed[0].audio_time as number) : null,
      endTime: timed.length ? (timed[timed.length - 1].audio_time as number) : null,
      contextBefore: join(gtoks.slice(Math.max(0, lo - CONTEXT_WORDS), lo)),
      contextAfter: join(gtoks.slice(hi + 1, hi + 1 + CONTEXT_WORDS)),
    };
  }

  function onMouseUp() {
    // Defer so the browser has finalised the selection.
    window.setTimeout(() => {
      const s = resolveSelection();
      if (!s) { setSelMenu(null); return; }
      const rect = window.getSelection()?.getRangeAt(0).getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) { setSelMenu(null); return; }
      // The whole rect, not just a y: selecting text near the foot of the
      // pane put this menu off the bottom, and a side can only be chosen
      // from something that knows where the selection starts as well.
      setSelMenu({ sel: s, rect });
    }, 0);
  }

  // Dismiss the menu on scroll or when the selection collapses elsewhere.
  useEffect(() => {
    if (!selMenu) return;
    const close = () => setSelMenu(null);
    const onSelChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) setSelMenu(null);
    };
    window.addEventListener("scroll", close, true);
    document.addEventListener("selectionchange", onSelChange);
    return () => {
      window.removeEventListener("scroll", close, true);
      document.removeEventListener("selectionchange", onSelChange);
    };
  }, [selMenu]);

  function playFromClick(e: React.MouseEvent<HTMLParagraphElement>, key: string) {
    if (!onPlayAt) return;
    // Don't hijack a text selection — only a plain click seeks.
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    const toks = timesByPara.get(key);
    if (!toks || toks.length === 0) return;
    const off = caretCharOffset(e.currentTarget, e.clientX, e.clientY);
    if (off == null) return;
    // The word at the click = the last token whose start is at/before the click.
    let chosen = toks[0];
    for (const t of toks) {
      if (t.char <= off) chosen = t;
      else break;
    }
    onPlayAt(chosen.time);
  }

  return (
    <div className={`manuscript${showBook ? " ms-formatted" : ""}${showBook && numbered ? " ms-numbered" : ""}${showBook && !ragged ? " ms-just" : ""}${gutter ? " sm-on" : ""}${original ? " ms-original" : ""}`}
      style={original ? ({ "--oz": String(zoom) } as React.CSSProperties) : undefined}
      ref={rootRef} onMouseUp={onFlagSelection && !original ? onMouseUp : undefined}>
      {(canFormat || hasMarks || hasComments || originalOk) && (
        <div className="ms-viewbar">
          {curPage != null && (
            <div className="ms-pagepick">
              <button type="button" className={`ms-pagepill${pageMenu ? " open" : ""}`} aria-haspopup="menu" aria-expanded={pageMenu}
                onClick={() => setPageMenu((v) => !v)} title="The page you're on — click to go to a page or see the original">
                <Icon name="file-text" size={14} />
                <b>p. {curPage}</b>{pageTotal ? <span className="ms-pagepill-of">/ {pageTotal}</span> : null}
                <Icon name={pageMenu ? "chevron-up" : "chevron-down"} size={13} />
              </button>
              {pageMenu && (
                <PageMenu
                  page={curPage}
                  total={pageTotal}
                  range={chapterId && shownPages.length ? [shownPages[0], shownPages[shownPages.length - 1]] : null}
                  original={original}
                  originalOk={originalOk}
                  pdfHref={pdfUrl ? (n) => `${pdfUrl}#page=${pdfPageOf(n)}` : null}
                  onGo={goToPage}
                  onOriginal={() => switchView(original ? (formatted && canFormat ? "formatted" : "flowing") : "original")}
                  onClose={() => setPageMenu(false)}
                />
              )}
            </div>
          )}
          {original && pageWords && (
            <label className="ms-tog" title="Ring each flag on its words on the page">
              <input type="checkbox" checked={flagsOn} onChange={(e) => setFlagsOn(e.target.checked)} /> Flags
            </label>
          )}
          {original && <span className="ms-viewnote">The page as uploaded, with its own notes and marks</span>}
          {!original && hasMarks && (
            <label className="ms-tog sm-tog" title="The script's notes, highlights and pen marks, beside and on the text. Click a note to see the page.">
              <input type="checkbox" checked={marksOn} onChange={(e) => setMarks(e.target.checked)} />
              <Icon name="pen-line" size={14} /> Script marks
            </label>
          )}
          {!original && hasComments && (
            <label className="ms-tog sm-tog" title="Word comments on the script - notes on the writing, not direction for the recording">
              <input type="checkbox" checked={commentsOn} onChange={(e) => setComments(e.target.checked)} />
              <Icon name="message" size={14} /> Comments
            </label>
          )}
          <span className="sm-tog-sep" aria-hidden="true" />
          {canFormat && !original && (<>
          {showBook && layoutState === "loading" && (
            <span className="ms-viewnote"><span className="spinner sm" /> Reading the page layout — first time only…</span>
          )}
          {showBook && layoutState === "error" && (
            <span className="ms-viewnote">Couldn't read the page layout, so some text shows unformatted.</span>
          )}
          {showBook && layoutState === "done" && coverage && coverage.matched === 0 && !hasInline && (
            <span className="ms-viewnote">This take was proofed against an older reading of the manuscript — re-proof it to format it.</span>
          )}
          <label className={`ms-tog${showBook ? "" : " off"}`} title="Number the lines of each page, as printed">
            <input type="checkbox" checked={numbered} disabled={!showBook} onChange={(e) => setNums(e.target.checked)} /> Line numbers
          </label>
          <label className={`ms-tog${showBook ? "" : " off"}`} title="Leave the right edge uneven instead of justified">
            <input type="checkbox" checked={ragged} disabled={!showBook} onChange={(e) => setRag(e.target.checked)} /> Ragged right
          </label>
          </>)}
          {(canFormat || originalOk) && (
          <div className={`ms-zoom${showBook || original ? "" : " off"}`} role="group" aria-label="Zoom">
            <button type="button" disabled={!(showBook || original) || zoom <= ZOOM_STEPS[0]} onClick={() => stepZoom(-1)}
              title="Zoom out" aria-label="Zoom out"><Icon name="zoom-out" size={15} /></button>
            <button type="button" className="ms-zoom-val" disabled={!(showBook || original) || zoom === 1} onClick={() => zoomTo(1)}
              title="Back to the size that fits the pane">{Math.round(zoom * 100)}%</button>
            <button type="button" disabled={!(showBook || original) || zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]} onClick={() => stepZoom(1)}
              title="Zoom in" aria-label="Zoom in"><Icon name="zoom-in" size={15} /></button>
          </div>
          )}
          {(canFormat || originalOk) && (
          <div className="ms-seg" role="group" aria-label="Manuscript view">
            {canFormat && (
              <button type="button" className={showBook ? "on" : ""} aria-pressed={showBook}
                onClick={() => switchView("formatted")}
                title="Paragraphs, line breaks and headings as the printed page sets them">Formatted</button>
            )}
            <button type="button" className={!showBook && !original ? "on" : ""} aria-pressed={!showBook && !original}
              onClick={() => switchView("flowing")}>Text only</button>
            {originalOk && (
              <button type="button" className={`ms-seg-orig${original ? " on" : ""}`} aria-pressed={original}
                disabled={!pdfPages} onClick={() => switchView("original")}
                title="The original PDF's pages, as uploaded — flags drawn on their words">
                <Icon name="file-text" size={13} /> Original
              </button>
            )}
          </div>
          )}
        </div>
      )}
      {original && projectId ? (
        <OriginalPages
          pages={shownPages}
          pageOffset={pageOffset}
          projectId={projectId}
          sizes={pdfPages?.sizes}
          fp={pdfPages?.fp}
          boxes={pageWords?.boxes}
          tokens={manuscript.tokens || []}
          discrepancies={discrepancies}
          categories={categories}
          selectedId={selectedId}
          activeId={activeId}
          onSelect={onSelect}
          onPlayAt={onPlayAt}
          flagsOn={flagsOn}
          zoom={zoom}
          find={find}
        />
      ) : manuscript.pages.map((page) => {
        const frame = showBook ? frameFor(page) : null;
        // Book pages: a sheet with the book's own running head, its page number
        // at the side the book printed it, and a chapter opening sunk down the
        // page as it was. The page number stays a link to that page of the PDF.
        const folio = pdfUrl ? (
          <a href={`${pdfUrl}#page=${Math.max(1, page.page_number - pageOffset)}`} target="_blank"
            rel="noopener noreferrer" title={`Open p. ${page.page_number} in the original PDF`}>
            {page.page_number}
          </a>
        ) : <>{page.page_number}</>;
        const side = frame?.folio ?? (frame?.head ? (page.page_number % 2 ? "r" : "l") : "b");
        const sheetCls = [
          "ms-page",
          showBook ? "ms-sheet" : "",
          showBook && frame?.sink ? (frame.full ? "ms-sunk-full" : "ms-sunk") : "",
        ].filter(Boolean).join(" ");
        const sinkStyle = showBook && frame?.sink
          ? ({ "--ms-sink": String(frame.sink) } as React.CSSProperties) : undefined;
        return (
        <div key={page.page_number} className={sheetCls} data-page={page.page_number} style={sinkStyle}>
          {showBook ? (
            <>
              {frame?.head && (
                <div className={`ms-runhead ms-folio-${side}`} aria-label={`Page ${page.page_number}`}>
                  <span className="ms-folio">{side === "l" && folio}</span>
                  <span className="ms-headtext">{frame.head}</span>
                  <span className="ms-folio">{side === "r" && folio}</span>
                </div>
              )}
              {!frame?.head && <div className="ms-folio-foot">{folio}</div>}
            </>
          ) : (
          <div className="ms-page-num">
            {pdfUrl ? (
              <a
                href={`${pdfUrl}#page=${Math.max(1, page.page_number - pageOffset)}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open p. ${page.page_number} in the original PDF`}
              >
                p. {page.page_number}
                <span className="ms-page-ext" aria-hidden="true"><Icon name="file-text" size={11} /></span>
              </a>
            ) : (
              <>p. {page.page_number}</>
            )}
          </div>
          )}
          {(() => {
            const model = showBook ? pageModel(page, layoutFor) : null;
            return page.paragraphs.map((para, pi) => {
              const key = `${page.page_number}:${pi}`;
              const marks = marksByPara.get(key) || [];
              const playable = !!onPlayAt && (timesByPara.get(key)?.length ?? 0) > 0;
              const pm = model?.paras[pi] ?? null;
              return (
                <p
                  key={pi}
                  data-page={page.page_number}
                  data-para={pi}
                  className={`ms-para kind-${para.kind}${para.include_in_compare ? "" : " excluded"}${playable ? " playable" : ""}${showBook && !pm ? " ms-unlaid" : ""}`}
                  onClick={playable ? (e) => playFromClick(e, key) : undefined}
                >
                  {pm
                    ? renderFormatted(para.text, pm, marks, activeId, selectedId, onSelect)
                    : renderText(para.text, marks, activeId, selectedId, onSelect)}
                </p>
              );
            });
          })()}
          {notesByPage.has(page.page_number) && (
            <div className="sm-layer" aria-label="The script's notes on this page">
              {notesByPage.get(page.page_number)!.map((m) => (
                <MarginNote key={m.id} m={m} onHover={setFocusMark}
                  onOpen={(mk, el) => setOpenMark({ mark: mk, rect: el.getBoundingClientRect() })} />
              ))}
            </div>
          )}
        </div>
        );
      })}

      {openMark && projectId && chapterId && (
        <MarkPopover projectId={projectId} chapterId={chapterId} mark={openMark.mark} anchor={openMark.rect}
          pdfUrl={pdfUrl} onClose={() => setOpenMark(null)} />
      )}

      {selMenu && (
        <div
          className="sel-menu"
          style={anchored(selMenu.rect, { width: onFindAll ? 300 : 220 })}
          // Keep the text selection alive while interacting with the menu.
          onMouseDown={(e) => e.preventDefault()}
        >
          {onFlagSelection && (
            <button
              className="sel-item"
              onClick={() => { onFlagSelection(selMenu.sel); window.getSelection()?.removeAllRanges(); setSelMenu(null); }}
            >
              ⚑ Flag here
            </button>
          )}
          {onPlayAt && selMenu.sel.startTime != null && (
            <button
              className="sel-item"
              onClick={() => { onPlayAt(selMenu.sel.startTime as number); setSelMenu(null); }}
            >
              ▶ Play selection
            </button>
          )}
          {onFindAll && projectId && findable(selMenu.sel.query) && (
            <button
              className="sel-item sel-find"
              onClick={() => { onFindAll(selMenu.sel); window.getSelection()?.removeAllRanges(); setSelMenu(null); }}
            >
              <Icon name="search" size={14} />
              <span className="sel-grow">Find all of these in the manuscript</span>
              <FindCount projectId={projectId} query={selMenu.sel.query} />
            </button>
          )}
          <div className="sel-sep" />
          <div className="sel-head">Look up pronunciation</div>
          {PRON_SOURCES.map((s) => (
            <a
              key={s.label}
              className="sel-item sel-link"
              href={s.url(selMenu.sel.query)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setSelMenu(null)}
            >
              {s.label} ↗
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// How many times the selected words are in the book - asked as the menu opens,
// and the same answer Find mode then opens with.
function FindCount({ projectId, query }: { projectId: string; query: string }) {
  const { data, isLoading } = useFind(projectId, query);
  if (isLoading) return <span className="sel-count wait" aria-label="Counting" />;
  if (!data) return null;
  return <span className="sel-count">{data.hits.length}{data.truncated ? "+" : ""}</span>;
}

// The page menu, from the page number in the bar: go to a page, see the
// original page in place, or open the PDF in a new tab.
function PageMenu({ page, total, range, original, originalOk, pdfHref, onGo, onOriginal, onClose }: {
  page: number;
  total: number | undefined;
  range: [number, number] | null;      // the pages this file reads (the report), else null
  original: boolean;
  originalOk: boolean;
  pdfHref: ((n: number) => string) | null;
  onGo: (n: number) => boolean;
  onOriginal: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(String(page));
  const [missing, setMissing] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node) && !(e.target as Element).closest?.(".ms-pagepill")) onClose();
    };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", key); };
  }, [onClose]);
  const go = () => {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n)) return;
    if (onGo(n)) { onClose(); return; }
    setMissing(n);
  };
  return (
    <div ref={box} className="ms-pagemenu" role="menu" aria-label={`Page ${page}`}>
      <div className="ms-pagemenu-head">
        <b>Page {page}</b>
        <span>{total ? `of ${total}` : ""}{range ? ` · this file reads pp. ${range[0]}–${range[1]}` : ""}</span>
      </div>
      <form className="ms-pagemenu-go" onSubmit={(e) => { e.preventDefault(); go(); }}>
        <label htmlFor="ms-go-page">Go to page</label>
        <input id="ms-go-page" inputMode="numeric" value={draft} onChange={(e) => { setDraft(e.target.value); setMissing(null); }} autoFocus
          onFocus={(e) => e.currentTarget.select()} aria-label="Page number" />
        <button type="submit" className="btn sm">Go</button>
      </form>
      {missing != null && (
        <div className="ms-pagemenu-note">
          p. {missing} isn't in {range ? `this file — it reads pp. ${range[0]}–${range[1]}` : "this text"}.
          {pdfHref && <> <a href={pdfHref(missing)} target="_blank" rel="noopener noreferrer" onClick={onClose}>Open it in the PDF ↗</a></>}
        </div>
      )}
      <div className="ms-pagemenu-sep" />
      {originalOk && (
        <button type="button" role="menuitem" className="ms-pagemenu-item primary" onClick={() => { onOriginal(); onClose(); }}>
          <Icon name="rotate-cw" size={17} />
          <span>
            {original ? "Show the text again" : "Show the original PDF"}
            <small>{original ? "Back to the manuscript text, on this page"
              : "Changes the display to the original manuscript page with annotations, if any"}</small>
          </span>
        </button>
      )}
      {pdfHref && (
        <a role="menuitem" className="ms-pagemenu-item" href={pdfHref(page)} target="_blank" rel="noopener noreferrer" onClick={onClose}>
          <Icon name="external" size={17} />
          <span>Open the original PDF in a new tab</span>
        </a>
      )}
    </div>
  );
}

// Marks are character ranges over the whole paragraph. The formatted view
// renders that paragraph in pieces (one element per printed line), so each
// piece needs the marks that touch it, rebased to its own coordinates.
// Clipping — not dropping — is what lets a highlight span a line break.
function sliceMarks(marks: Mark[], a: number, b: number, isLast: boolean): Mark[] {
  const out: Mark[] = [];
  for (const m of marks) {
    if (m.caret) {
      // zero-width: belongs to the piece it sits inside
      if (m.start >= a && (m.start < b || (isLast && m.start === b))) {
        out.push({ ...m, start: m.start - a, end: m.start - a });
      }
      continue;
    }
    const s = Math.max(m.start, a);
    const e = Math.min(m.end, b);
    if (e > s) out.push({ ...m, start: s - a, end: e - a });
  }
  return out;
}

function renderText(
  text: string,
  marks: Mark[],
  activeId: string | null,
  selectedId: string | null,
  onSelect: (id: string) => void
) {
  if (!marks.length) return text;
  const out: React.ReactNode[] = [];
  let cursor = 0;
  marks.forEach((m, i) => {
    const pos = Math.max(m.start, cursor);
    const cls =
      `mark m-${m.colour ?? m.type}` +
      (m.caret ? " caret" : "") +
      (m.id === activeId ? " active" : "") +
      (m.id === selectedId ? " selected" : "");

    if (m.caret) {
      if (pos > cursor) { out.push(text.slice(cursor, pos)); cursor = pos; }
      const isPause = m.type === "pause";
      out.push(
        <mark
          key={`${i}-${m.id}`}
          className={cls}
          data-id={m.id}
          title={isPause ? (m.title || "Long pause") : (m.title ? `added: “${m.title}”` : undefined)}
          onClick={(e) => { e.stopPropagation(); onSelect(m.id); }}
        >
          {isPause ? "⏸︎" : "‸"}
        </mark>
      );
      return; // zero-width: don't advance the cursor
    }

    const start = pos;
    const end = Math.max(m.end, start);
    if (start > cursor) out.push(text.slice(cursor, start));
    out.push(
      <mark
        key={`${i}-${m.id}`}
        className={cls}
        data-id={m.id}
        onClick={(e) => { e.stopPropagation(); onSelect(m.id); }}
      >
        {text.slice(start, end)}
      </mark>
    );
    cursor = end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}


// ---------------------------------------------------------------------------
// Formatted view
// ---------------------------------------------------------------------------

type BlockRole = "prose" | "title" | "caps" | "sub";
interface BlockModel {
  lines: number[];
  heading: boolean;
  size: number;         // largest recorded size on the block, % of body
  titleable: boolean;
  upper: boolean;
  role: BlockRole;
  hstart: boolean;      // a heading group starts here (not at the top of the page)
  hend: boolean;        // ...and ends here
  afterHead: boolean;   // the first prose after a heading group that opens the page
}
interface ParaModel {
  lay: ParaLayout;
  starts: number[];
  blocks: BlockModel[];
  codes: string[][];
  nums: (number | undefined)[];
}

const isUpper = (s: string) => {
  const letters = s.replace(/[^\p{L}]/gu, "");
  return letters.length >= 2 && letters === letters.toLocaleUpperCase();
};

// One page, modelled before it is drawn: its printed blocks, which of them are
// headings and what part each plays, and its line numbers.
function pageModel(
  page: { page_number: number; paragraphs: ManuscriptParagraph[] },
  layoutFor: (pageNo: number, pi: number, para: ManuscriptParagraph) => ParaLayout | null
): { paras: (ParaModel | null)[] } {
  const paras: (ParaModel | null)[] = page.paragraphs.map((para, pi) => {
    const lay = layoutFor(page.page_number, pi, para);
    if (!lay?.line_starts?.length) return null;
    const text = para.text;
    const starts = lay.line_starts.filter((n) => n >= 0 && n <= text.length);
    const breaks = new Set(lay.para_starts || [0]);
    const codes = starts.map((_, i) => (lay.line_fmt?.[i] || "").split(" ").filter(Boolean));
    const blocks: BlockModel[] = [];
    starts.forEach((st, i) => {
      if (i === 0 || breaks.has(st)) {
        blocks.push({ lines: [], heading: false, size: 100, titleable: false, upper: false,
                      role: "prose", hstart: false, hend: false, afterHead: false });
      }
      blocks[blocks.length - 1].lines.push(i);
    });
    for (const b of blocks) {
      const cs = b.lines.map((i) => codes[i]);
      const titled = cs.some((c) => c.includes("t"));
      // A heading: every line set apart (centred, or another size) — or a line
      // standing alone that reads as one ("Chapter 3"), however it was set.
      b.heading = titled || cs.every((c) => c.some((x) => x === "c" || x[0] === "s"));
      for (const c of cs) for (const x of c) if (x[0] === "s") b.size = Math.max(b.size, Number(x.slice(1)));
      // Only type at least body size can be a chapter title: a small date line
      // standing alone mid-chapter must not be blown up into one.
      b.titleable = titled || b.size > 104;
      const a = starts[b.lines[0]];
      const last = b.lines[b.lines.length - 1];
      b.upper = isUpper(text.slice(a, last + 1 < starts.length ? starts[last + 1] : text.length));
    }
    return { lay, starts, blocks, codes, nums: [] };
  });

  // Headings are styled as a GROUP, across the page's paragraphs: the largest
  // block is the title; the rest are set in capitals if printed in capitals
  // (a point-of-view name) or as a quiet italic line (a time and place).
  const seq: BlockModel[] = [];
  paras.forEach((pm) => pm?.blocks.forEach((b) => seq.push(b)));
  for (let i = 0; i < seq.length; ) {
    if (!seq[i].heading) { i++; continue; }
    let j = i;
    while (j < seq.length && seq[j].heading) j++;
    const group = seq.slice(i, j);
    const titled = group.filter((b) => b.titleable);
    const title = titled.reduce<BlockModel | null>((a, b) => (!a || b.size > a.size ? b : a), null);
    group.forEach((b) => { b.role = b === title ? "title" : b.upper ? "caps" : "sub"; });
    group[0].hstart = i > 0;
    group[group.length - 1].hend = true;
    if (i === 0 && j < seq.length) seq[j].afterHead = true;
    i = j;
  }

  // Line numbers run down the PAGE, headings included, so "p. 28, line 14" is
  // the fourteenth line a reader sees there. Only when the whole page is laid
  // out — a paragraph read as flowing text would throw every count after it.
  const complete = paras.every((pm, pi) => pm || !page.paragraphs[pi].text.trim());
  let n = 0;
  paras.forEach((pm) => { if (pm) pm.nums = pm.starts.map(() => (complete ? ++n : undefined)); });
  return { paras };
}

// The paragraph's own text, broken exactly where the printed page broke it and
// set the way the page set it. Every character of `text` is emitted exactly
// once and in order; that is what keeps tokenAtPoint's character arithmetic —
// and so select-to-flag — working, since it just walks text nodes. Italics wrap
// text in <em> (still text nodes, in order); numbers and the printed hyphen are
// pseudo-elements, drawn rather than written.
function renderFormatted(
  text: string,
  pm: ParaModel,
  marks: Mark[],
  activeId: string | null,
  selectedId: string | null,
  onSelect: (id: string) => void
) {
  const { starts, blocks, codes, lay, nums } = pm;
  const ital = lay.italic || [];
  return blocks.map((b, bi) => {
    const cls = ["ms-vpara"];
    if (b.afterHead) cls.push("ms-after-head");
    if (b.role !== "prose") {
      cls.push("ms-vhead", `ms-h-${b.role}`);
      if (b.upper) cls.push("ms-h-upper");
      if (b.hstart) cls.push("ms-h-start");
      if (b.hend) cls.push("ms-h-end");
    }
    return (
      <span key={bi} className={cls.join(" ")}>
        {b.lines.map((i, li) => {
          const start = starts[i];
          const end = i + 1 < starts.length ? starts[i + 1] : text.length;
          const cs = codes[i];
          const style: React.CSSProperties = {};
          let lc = "ms-line";
          if (b.role === "prose") {
            for (const c of cs) {
              if (c === "c") style.textAlign = "center";
              else if (c === "r") style.textAlign = "right";
              else if (c[0] === "s") style.fontSize = `${Number(c.slice(1))}%`;
              // Neither "p" (a paragraph's first-line indent) nor "i" (a quote or
              // letter set in from the margin) is drawn: every block sits on the
              // same margin, told apart by the space between blocks instead.
            }
            // Justified like print: every line but a paragraph's last — unless
            // that last line ran the full measure too (it carries over the page).
            const aligned = cs.includes("c") || cs.includes("r");
            if (!aligned && (li < b.lines.length - 1 || cs.includes("j"))) lc += " jl";
          }
          if (cs.includes("h")) lc += " hy";
          return (
            <span key={i} className={lc} style={style} data-n={nums[i]}>
              {segments(text, start, end, ital, marks, activeId, selectedId, onSelect)}
            </span>
          );
        })}
      </span>
    );
  });
}

// A line's text cut at its italic runs; marks are clipped to each piece, so a
// highlight still runs straight through a change of style.
function segments(
  text: string, start: number, end: number, ital: number[][],
  marks: Mark[], activeId: string | null, selectedId: string | null, onSelect: (id: string) => void
) {
  const cuts = new Set<number>([start, end]);
  for (const [a, b] of ital) {
    if (a > start && a < end) cuts.add(a);
    if (b > start && b < end) cuts.add(b);
  }
  const pts = [...cuts].sort((x, y) => x - y);
  const out: React.ReactNode[] = [];
  for (let k = 0; k < pts.length - 1; k++) {
    const a = pts[k], b = pts[k + 1];
    const body = renderText(text.slice(a, b), sliceMarks(marks, a, b, b === text.length), activeId, selectedId, onSelect);
    out.push(ital.some(([x, y]) => x <= a && b <= y)
      ? <em key={a} className="ms-it">{body}</em>
      : <Fragment key={a}>{body}</Fragment>);
  }
  return out;
}
